import { FC, useCallback, useMemo, useState } from 'react';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './AlgorithmTargets.translations.json';
import { RequiredKeyDataType } from '../../schema';
import { Flex, Input } from 'antd';
import { AlgorithmTarget } from './AlgorithmTarget';
import { recursiveSearch } from '../../../common/utils/recursiveSearch';
import { useGetAlgorithmSourcesQuery } from '../sources/query/useGetAlgorithmSourcesQuery';
import { AlgorithmTargetsHeader } from './AlgorithmTargetsHeader';
import { AlgorithmTargetRules } from './AlgoritmTargetRules';
import { useGetAlgorithmTargetsQuery } from './query/useGetAlgorithmTargetsQuery';
import { AlgorithmTargetFormDataType } from './schema/types';
import { ValidationMessagesMap } from '../../../common/utils/ValidationMessagesMap';
import { ErrorAlertWithDrawer } from '../../../common/components/ErrorAlertWithDrawer';
import { useGetAlgorithmMappingsQuery } from '../mappings/query/useGetAlgorithmMappingsQuery';
import { AlgorithmHeader } from '../AlgorithmHeader';
import { getMapBy } from '../../../common/utils/getMapBy';
import { DataObjectDescriptionVersionStore } from '../../DataObjectDescriptionVersionStore';

const { addTranslations, useTranslation } = i18nNsFactory('algorithm-targets');
addTranslations(translations);

const sortTargets = (
  a: AlgorithmTargetFormDataType,
  b: AlgorithmTargetFormDataType,
  requiredKeysByKeys: Map<string, RequiredKeyDataType>,
) => {
  const aKey =
    requiredKeysByKeys.get(a.targetKey)?.sequenceNumber ??
    Number.MAX_SAFE_INTEGER;
  const bKey =
    requiredKeysByKeys.get(b.targetKey)?.sequenceNumber ??
    Number.MAX_SAFE_INTEGER;
  if (aKey === bKey) {
    return (
      (a.sequenceNumber ?? Number.MAX_SAFE_INTEGER) -
      (b.sequenceNumber ?? Number.MAX_SAFE_INTEGER)
    );
  }
  return aKey - bKey;
};

export interface AlgorithmTargetsProps {
  algorithmId: number;
  mutationErrors?: ValidationMessagesMap;
  requiredKeys?: RequiredKeyDataType[];
  onAddButtonClick?: () => void;
  onGraphButtonClick?: (id: number) => void;
  editGraphLabel?: string;
  onEditButtonClick?: (id: number) => void;
  onCloneButtonClick?: (id: number) => void;
  onDeleteButtonClick?: (id: number) => void;
  expanded?: boolean;
  onToggle?: (expanded: boolean) => void;
  targetId?: number;
}

export const AlgorithmTargets: FC<AlgorithmTargetsProps> = (props) => {
  const {
    algorithmId,
    mutationErrors,
    onAddButtonClick,
    onGraphButtonClick,
    editGraphLabel,
    onEditButtonClick,
    onCloneButtonClick,
    onDeleteButtonClick,
    expanded,
    onToggle,
    targetId,
  } = props;
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [dataObjectDescriptionVersion] =
    DataObjectDescriptionVersionStore.useValue('dataObjectDescriptionVersion');
  const { data: algorithmTargets, isLoading: isLoadingTargets } =
    useGetAlgorithmTargetsQuery({
      id: algorithmId,
    });

  const { data: algorithmSources, isLoading: isLoadingSources } =
    useGetAlgorithmSourcesQuery({
      id: algorithmId,
    });

  const { data: algorithmMappings, isLoading: isLoadingMappings } =
    useGetAlgorithmMappingsQuery({
      id: algorithmId,
    });

  const isLoading = isLoadingTargets || isLoadingSources || isLoadingMappings;

  const handleSearch = useCallback(
    (value: string) => {
      setSearch(value);
    },
    [setSearch],
  );

  const requiredKeysByKeys = useMemo(
    () => getMapBy(dataObjectDescriptionVersion.requiredKeys.reverse(), 'key'),
    [dataObjectDescriptionVersion],
  );

  const visibleTargets = useMemo(
    () =>
      (algorithmTargets?.targets || [])
        .sort((a, b) => sortTargets(a, b, requiredKeysByKeys))
        .filter((target) => recursiveSearch(target, search)),
    [algorithmTargets, search, requiredKeysByKeys],
  );

  return (
    <AlgorithmTargetsHeader
      algorithmId={algorithmId}
      count={algorithmTargets?.targets?.length}
      filteredCount={visibleTargets.length}
      onAddButtonClick={onAddButtonClick ? () => onAddButtonClick() : undefined}
      expanded={expanded}
      onToggle={onToggle}
      isLoading={isLoading ? 'manage' : undefined}
      hideExpand={!algorithmTargets?.targets?.length}
    >
      {Boolean(algorithmTargets?.targets?.length) && (
        <>
          <ErrorAlertWithDrawer
            validationMessagesMap={
              mutationErrors ?? new ValidationMessagesMap()
            }
          />
          <Flex vertical wrap="wrap" gap="small">
            <Input.Search
              id="target-search"
              placeholder={t('search.placeholder')}
              onSearch={handleSearch}
              allowClear
            />
            {visibleTargets.map((target, index) => (
              <AlgorithmHeader
                algorithmId={algorithmId}
                expanded={targetId === target.id}
                key={index}
                id={'algorithm-target-' + index}
                title={t('target') + ' ' + target.sequenceNumber}
                onEditGraphClick={
                  onGraphButtonClick
                    ? () => onGraphButtonClick(target.id)
                    : undefined
                }
                editGraphLabel={editGraphLabel}
                onEditButtonClick={
                  onEditButtonClick
                    ? () => onEditButtonClick(target.id)
                    : undefined
                }
                onCloneButtonClick={
                  onCloneButtonClick
                    ? () => onCloneButtonClick(target.id)
                    : undefined
                }
                onDeleteButtonClick={
                  onDeleteButtonClick
                    ? () => onDeleteButtonClick(target.id)
                    : undefined
                }
              >
                <AlgorithmTarget
                  target={target}
                  mappings={algorithmMappings?.mappings || []}
                  requiredKeysByKeys={requiredKeysByKeys}
                >
                  <AlgorithmTargetRules
                    rules={target.rules}
                    sources={algorithmSources?.sources || []}
                    mappings={algorithmMappings?.mappings || []}
                    isLoading={isLoading}
                  />
                </AlgorithmTarget>
              </AlgorithmHeader>
            ))}
          </Flex>
        </>
      )}
    </AlgorithmTargetsHeader>
  );
};
